import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, DollarSign, Loader2, Radio } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const WINDOWS = [
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "all", label: "All Time" },
];

export default function LeaderboardPage() {
  const [window, setWindow] = useState("week");
  const [genre, setGenre] = useState("all");
  const [genres, setGenres] = useState([{ id: "all", label: "All Genres", color: "#00F0FF", dj_count: 0 }]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard/genres`)
      .then(r => setGenres(r.data.genres || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/leaderboard?window=${window}&genre=${genre}`)
      .then(r => setData(r.data))
      .catch(() => setData({ rows: [], window, genre, since: null }))
      .finally(() => setLoading(false));
  }, [window, genre]);

  const rows = data?.rows || [];
  const maxTotal = Math.max(1, ...rows.map(r => r.total_cents));
  const activeGenre = genres.find(g => g.id === genre) || genres[0];

  const rankColor = (i) => (
    i === 0 ? "#FFC107" : i === 1 ? "#94A3B8" : i === 2 ? "#FF8C00" : "#00F0FF"
  );

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="leaderboard-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FFC107] to-[#FF2A6D] flex items-center justify-center"><Trophy className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#FFC107]">DECK</span> · LEADERBOARD</div>
        </Link>
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white">← Studio</Link>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFC107] mb-2 flex items-center gap-2"><Trophy className="w-3 h-3"/> // Top Earners{activeGenre && activeGenre.id !== "all" && (<> · <span style={{color: activeGenre.color}}>{activeGenre.label}</span></>)}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Who's crushing it?</h1>
          <p className="text-[#94A3B8] mt-2 max-w-2xl">DJs ranked by combined tips + merch during live sessions and via the public Tip Jar. Filter by genre so hip-hop competes with hip-hop.</p>
        </div>

        <div className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">// Genre</div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="leaderboard-genre-tabs">
            {genres.map(g => (
              <button key={g.id} data-testid={`genre-${g.id}`} onClick={() => setGenre(g.id)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  genre === g.id ? "text-black" : "text-[#94A3B8] hover:text-white bg-transparent"
                }`}
                style={genre === g.id
                  ? { background: g.color, borderColor: g.color }
                  : { borderColor: "#2A354B" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.color }}/>
                {g.label}
                {g.dj_count > 0 && genre !== g.id && (
                  <span className="text-[9px] opacity-60">{g.dj_count}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2" data-testid="leaderboard-window-tabs">
          {WINDOWS.map(w => (
            <button key={w.key} data-testid={`window-${w.key}`} onClick={() => setWindow(w.key)}
              className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-colors ${
                window === w.key
                  ? "bg-[#FFC107] border-[#FFC107] text-black"
                  : "bg-transparent border-[#2A354B] text-[#94A3B8] hover:text-white"
              }`}>{w.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#FFC107]"/></div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-8 text-center space-y-2" data-testid="leaderboard-empty">
            <Radio className="w-8 h-8 text-[#94A3B8] mx-auto"/>
            <div className="font-display font-black text-xl">No earnings yet {activeGenre && activeGenre.id !== "all" && (<span className="text-[#94A3B8]">in {activeGenre.label}</span>)}</div>
            <div className="text-sm text-[#94A3B8]">Go live, drop merch, or share your /tip/@handle QR to appear here.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-rows">
            {rows.map((r, i) => {
              const gm = genres.find(g => g.id === r.genre);
              return (
              <Link key={r.dj_user_id} to={`/u/${r.dj_user_id}`} data-testid={`leaderboard-row-${i}`}
                className="block rounded-xl border border-[#1E2638] bg-[#121620] hover:bg-[#1A202C] hover:border-[#FFC107] transition-colors p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center font-display font-black text-2xl"
                    style={{ background: `${rankColor(i)}22`, color: rankColor(i), border: `2px solid ${rankColor(i)}` }}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-lg truncate">{r.display_name}</div>
                      {gm && (
                        <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
                          style={{ color: gm.color, borderColor: `${gm.color}55`, background: `${gm.color}11` }}
                          data-testid={`row-genre-${i}`}>
                          {gm.label}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                      Tips ${(r.tips_cents / 100).toFixed(2)} · Merch ${(r.merch_cents / 100).toFixed(2)} · {r.tx_count} tx
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-2xl text-[#FFC107]">${(r.total_cents / 100).toFixed(2)}</div>
                    <div className="mt-1 h-1.5 w-32 rounded-full bg-[#0B0E14] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#FFC107] to-[#FF2A6D]"
                        style={{ width: `${Math.min(100, (r.total_cents / maxTotal) * 100)}%` }}/>
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
